// frontend/packages/business-console/src/pages/Pulse/sections/VisitorsSection.jsx
//
// Pulse "Visitors" card. Shows today's site activity at a glance +
// yesterday's conversion rate for context + a 14-day session sparkline.
// Opens the full /visitors deep-dive page on click.
//
// See docs/design/site-analytics.md in the platform repo.

import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { Link as RouterLink } from 'react-router-dom';
import SectionShell from './SectionShell';
import KpiTile from './KpiTile';

function formatPct(rate) {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return '–';
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * Tiny inline sparkline. Renders a 14-segment polyline scaled to its own
 * container; no chart library to keep the bundle small.
 */
function Sparkline({ points }) {
  if (!points || points.length === 0) {
    return (
      <Box
        sx={{
          height: 28,
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: 12,
        }}
      >
        no data yet
      </Box>
    );
  }
  const W = 120;
  const H = 28;
  const max = Math.max(1, ...points.map((p) => p.sessions || 0));
  const step = points.length > 1 ? W / (points.length - 1) : W;
  const path = points
    .map((p, i) => {
      const x = i * step;
      const y = H - (p.sessions / max) * H;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
  return (
    <Box sx={{ height: H, lineHeight: 0 }}>
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.85"
        />
      </svg>
    </Box>
  );
}

export default function VisitorsSection({ visitors }) {
  if (!visitors) {
    return (
      <SectionShell
        title="Visitors"
        subtitle="Your website traffic"
        icon={<InsightsOutlinedIcon />}
        empty
        emptyCopy="As soon as visitors land on your site, you'll see today's sessions and conversions here."
      />
    );
  }

  const today = visitors.today || {};
  const yesterday = visitors.yesterday;
  const sessions = today.sessions || 0;
  const formSubmits = today.formSubmits || 0;

  return (
    <SectionShell
      title="Visitors"
      subtitle="Your website today"
      icon={<InsightsOutlinedIcon />}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
        }}
      >
        <KpiTile
          value={sessions}
          label="Sessions today"
          accent={sessions > 0 ? 'primary.main' : 'text.primary'}
        />
        <KpiTile
          value={formSubmits}
          label="Form submits today"
          accent={formSubmits > 0 ? 'success.main' : 'text.primary'}
        />
      </Box>

      <Divider sx={{ mt: 0.5 }} />

      <Stack spacing={1}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Last 14 days
          </Typography>
          <Box sx={{ color: 'primary.main' }}>
            <Sparkline points={visitors.sparkline} />
          </Box>
        </Box>
        {yesterday ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              color: 'text.secondary',
            }}
          >
            <span>Yesterday: {yesterday.sessions} sessions</span>
            <span>Conversion: {formatPct(yesterday.conversionRate)}</span>
          </Box>
        ) : null}
        {yesterday && yesterday.aiCrawlerHits > 0 ? (
          <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
            AI crawlers hit you {yesterday.aiCrawlerHits}× yesterday
          </Box>
        ) : null}
      </Stack>

      <Box
        component={RouterLink}
        to="../visitors"
        sx={{
          alignSelf: 'flex-start',
          mt: 'auto',
          color: 'primary.main',
          fontSize: 13,
          fontWeight: 600,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        Open Visitors →
      </Box>
    </SectionShell>
  );
}
